import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, CheckCircle, ArrowRight, DollarSign, Percent, Calendar, CreditCard, FileText, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const equipmentData = {
  "bin-cleaning": {
    name: "Trash Bin Cleaning Truck",
    image: "https://customer-assets.emergentagent.com/job_trashcapital/artifacts/ycxaot7k_Bin%20Cleaning.png",
    seoHeadline: "Scale Your Service with Professional Trash Bin Cleaning Truck Financing",
    description: "Capture the growing demand for eco-friendly hygiene. Finance your next bin-washing truck or trailer with $0 down options and rapid, same-day approvals.",
    features: [
      { label: "Financing Limit", value: "$20,000 – $750,000+", icon: DollarSign },
      { label: "Interest Rates", value: "Competitive Fixed Rates", icon: Percent },
      { label: "Loan Terms", value: "36 to 84 Months", icon: Calendar },
      { label: "Startup Friendly", value: "New Business Programs", icon: CreditCard },
      { label: "Tax Benefit", value: "100% Section 179 Write-Off", icon: FileText }
    ],
    inventory: [
      "Dual & Single Bin Truck Systems",
      "Trash Bin Cleaning Trailers",
      "High-Pressure Wash Systems",
      "Eco-Friendly Filtration Units",
      "New & Used Mobile Wash Rigs"
    ],
    faqs: [
      { question: "Do you finance startups in the bin cleaning industry?", answer: "Yes! We have specialized programs for new businesses. While terms may vary, we frequently help new entrepreneurs secure their first truck with a solid business plan and decent credit." },
      { question: "What if I'm buying a used cleaning trailer?", answer: "We provide financing for both new and used equipment. Whether you're buying from a major manufacturer like SparkleWash or a private seller, we can facilitate the loan." }
    ],
    loanRange: "$20,000 - $750,000+",
    customSteps: [
      { n: "1", title: "INQUIRE", text: "Apply online in 5 minutes with no hard credit pull." },
      { n: "2", title: "CUSTOMIZE", text: "Select your terms and payment structure." },
      { n: "3", title: "APPROVE", text: "Receive your funding commitment in hours." },
      { n: "4", title: "WASH", text: "We fund the manufacturer; you get to work." }
    ],
    ctaHeadline: "LAUNCH OR EXPAND YOUR CLEANING EMPIRE",
    ctaSubtext: "Don't let equipment costs hold your territory back. Secure your funding today."
  },
  "dumpster": {
    name: "Roll-Off Dumpster",
    image: "https://customer-assets.emergentagent.com/job_trashcapital/artifacts/ndkuqcti_Dumpsters.png",
    seoHeadline: "Scale Your Hauling Business with Flexible Roll-Off Dumpster Financing",
    description: "Stop waiting on capital to grow. Get 100% financing for new or used roll-off containers with terms built specifically for the waste management industry.",
    features: [
      { label: "Financing Limit", value: "$10,000 – $500,000+", icon: DollarSign },
      { label: "Interest Rates", value: "Competitive Market Rates", icon: Percent },
      { label: "Loan Terms", value: "24 to 72 Months", icon: Calendar },
      { label: "Down Payment", value: "$0 Down Options Available", icon: CreditCard },
      { label: "Tax Benefit", value: "Section 179 Eligible", icon: FileText }
    ],
    inventory: [
      "Standard 10-40 Yard Roll-Offs",
      "Concrete Washout Boxes",
      "Sealed Sludge Containers",
      "Stationary & Self-Contained Compactors"
    ],
    faqs: [
      { question: "Can I finance used roll-off containers?", answer: "Yes. We finance both new and used equipment from dealers or private party sellers with no strict age restrictions." },
      { question: "How much documentation is required?", answer: "For most requests under $250,000, we only need a simple application and your last 3 months of business bank statements." }
    ],
    loanRange: "$10,000 - $500,000+"
  },
  "parking-lot-sweeper": {
    name: "Parking Lot Sweeper",
    image: "https://customer-assets.emergentagent.com/job_trashcapital/artifacts/2ew1497c_Parking%20lot%20sweeper%20truck.jpg",
    seoHeadline: "Drive Your Curb Appeal with Specialized Parking Lot Sweeper Financing",
    description: "Maintain peak property standards without depleting your cash reserves. Secure 100% financing for new or used regenerative air and vacuum sweepers with flexible terms built for property maintenance fleets.",
    features: [
      { label: "Financing Limit", value: "$15,000 – $600,000+", icon: DollarSign },
      { label: "Interest Rates", value: "Fixed Rates from 7.49%", icon: Percent },
      { label: "Loan Terms", value: "24 to 72 Months", icon: Calendar },
      { label: "Down Payment", value: "$0 Down & Deferred Options", icon: CreditCard },
      { label: "Tax Advantage", value: "Full Section 179 Deduction", icon: FileText }
    ],
    inventory: [
      "Regenerative Air Sweepers",
      "Vacuum & Mechanical Broom Sweepers",
      "Ride-On & Compact Industrial Sweepers",
      "Parking Area & Street Sweeper Hybrids",
      "New & Used Equipment (All Makes/Models)"
    ],
    faqs: [
      { question: "Can I finance a used sweeper from a private seller?", answer: "Yes. We provide financing for equipment purchased from both authorized dealers and private party sellers, ensuring the unit passes a basic condition inspection." },
      { question: "How do seasonal payments work?", answer: "We understand the property maintenance cycle. We offer skip-payment or seasonal structures that allow you to lower your overhead during slower months." }
    ],
    loanRange: "$15,000 - $600,000+",
    customSteps: [
      { n: "1", title: "APPLY", text: "Complete our quick 'Application-Only' form online." },
      { n: "2", title: "REVIEW", text: "Receive a credit decision in as little as 2-4 hours." },
      { n: "3", title: "VERIFY", text: "Submit your invoice or quote from any vendor." },
      { n: "4", title: "DEPLOY", text: "Funds are wired to the seller within 24-48 hours." }
    ],
    ctaHeadline: "READY TO EXPAND YOUR FLEET?",
    ctaSubtext: "Join the facility maintenance leaders who rely on Waste Loan Bros for fast, industry-specific financing."
  },
  "roll-off-dumpster-truck": {
    name: "Roll-Off Dumpster Truck",
    image: "https://customer-assets.emergentagent.com/job_trashcapital/artifacts/3fuehobe_Roll%20Off%20Dumpster%20Trucks.png",
    seoHeadline: "Power Your Fleet with Flexible Roll-Off Truck Financing",
    description: "Whether you need a single-axle cable hoist or a heavy-duty tri-axle hook lift, Waste Loan Bros provides the capital to keep your containers moving. Get funded in as little as 24 hours.",
    features: [
      { label: "Financing Limit", value: "$50,000 – $1.5M+", icon: DollarSign },
      { label: "Interest Rates", value: "Fixed Rates from 6.99%", icon: Percent },
      { label: "Loan Terms", value: "36 to 84 Months", icon: Calendar },
      { label: "Down Payment", value: "Low to $0 Down Programs", icon: CreditCard },
      { label: "Asset Type", value: "New & Used Trucks", icon: FileText }
    ],
    inventory: [
      "Cable Hoist Roll-Off Trucks",
      "Hook Lift Systems",
      "Tri-Axle & Quad-Axle Heavy Haulers",
      "Single Axle & Extended Frame Models",
      "Chassis-Only or Complete Rig Financing"
    ],
    faqs: [
      { question: "Can I finance a truck and containers together?", answer: "Absolutely. We often bundle the truck chassis, hoist system, and a starting inventory of roll-off boxes into a single, manageable monthly payment to preserve your working capital." },
      { question: "What is the age limit for used trucks?", answer: "While many lenders stop at 7-10 years, Waste Loan Bros looks at the overall condition and work-readiness of the asset. We regularly finance older, well-maintained Mack, Kenworth, and Peterbilt roll-offs." }
    ],
    loanRange: "$50,000 - $1.5M+",
    customSteps: [
      { n: "1", title: "APPLY", text: "Complete our secure digital application in minutes." },
      { n: "2", title: "APPROVE", text: "Get a dedicated specialist to review and approve your file." },
      { n: "3", title: "SECURE", text: "E-sign your documents and verify your truck choice." },
      { n: "4", title: "DRIVE", text: "We wire funds directly to the dealer or private seller." }
    ],
    ctaHeadline: "STOP LEAVING REVENUE ON THE CURB",
    ctaSubtext: "Get the horsepower you need to dominate your route. Apply now for a same-day approval."
  },
  "side-loader": {
    name: "Side, Front & Rear Loader Trash Trucks",
    image: "https://customer-assets.emergentagent.com/job_trashcapital/artifacts/on69uusf_Front%20Loader%20Trash%20truck.png",
    seoHeadline: "Comprehensive Financing for Front, Side, and Rear Loader Trash Trucks",
    description: "From automated residential routes to heavy commercial pickups, Waste Loan Bros provides specialized capital for the industry's most essential assets. Scale your route with 100% financing and industry-leading terms.",
    features: [
      { label: "Financing Limit", value: "$100,000 – $2.5M+", icon: DollarSign },
      { label: "Interest Rates", value: "Competitive Fixed Rates", icon: Percent },
      { label: "Loan Terms", value: "Up to 84 Months", icon: Calendar },
      { label: "Down Payment", value: "$0 Down for Qualified", icon: CreditCard },
      { label: "Equipment Type", value: "New & Used Inventory", icon: FileText }
    ],
    inventory: [
      "Front Loaders (FEL) for Commercial Routes",
      "Automated Side Loaders (ASL) for Residential",
      "Manual & Semi-Automated Rear Loaders",
      "Dual-Stream & Recyclable Collection Units",
      "Chassis Support: Mack, Peterbilt, Autocar & More"
    ],
    faqs: [
      { question: "Do you offer financing for used rear or side loaders?", answer: "Yes. We specialize in financing used refuse equipment. Whether you are buying from a dealer or a private municipality, we can provide terms for vehicles up to 15 years old depending on condition." },
      { question: "Can I get funding for a startup waste company?", answer: "Absolutely. While many banks shy away from new ventures, Waste Loan Bros has specific programs for startups with 10%–20% down payments or strong personal credit backing." }
    ],
    loanRange: "$100,000 - $2.5M+",
    customSteps: [
      { n: "1", title: "APPLY", text: "Submit your business details via our secure portal." },
      { n: "2", title: "APPROVE", text: "Get a credit decision tailored to your route's revenue." },
      { n: "3", title: "EXECUTE", text: "E-sign documents and confirm equipment specs." },
      { n: "4", title: "ROUTE", text: "Funding is deployed; get your trucks on the road." }
    ],
    ctaHeadline: "SCALE YOUR FLEET CAPACITY TODAY",
    ctaSubtext: "Get the reliable trucks you need to service more stops and increase your bottom line."
  },
  "waste-pump-truck": {
    name: "Septic & Portable Sanitation Vacuum Trucks",
    image: "https://customer-assets.emergentagent.com/job_trashcapital/artifacts/pyhh4plg_Porta%20Potta%20Vacuum%20truck.jpg",
    seoHeadline: "Reliable Financing for Septic Pumper & Portable Sanitation Service Trucks",
    description: "Keep the liquid waste flowing and your business growing. Waste Loan Bros offers specialized 100% financing for septic vacuum trucks and portable toilet service rigs, including new builds and used units.",
    features: [
      { label: "Financing Limit", value: "$25,000 – $750,000+", icon: DollarSign },
      { label: "Interest Rates", value: "Competitive Fixed Terms", icon: Percent },
      { label: "Loan Terms", value: "24 to 84 Months", icon: Calendar },
      { label: "Down Payment", value: "$0 Down for Tier 1 & 2", icon: CreditCard },
      { label: "Tax Advantage", value: "IRS Section 179 Deductible", icon: FileText }
    ],
    inventory: [
      "Portable Sanitation Service Trucks (Pumper/Water Combo)",
      "Septic Pumper Trucks (Steel & Aluminum Tanks)",
      "Slide-In Vacuum Units for Pickup Trucks",
      "Cesspool Emptiers & Liquid Waste Haulers",
      "High-Capacity Industrial Vacuum Tankers"
    ],
    faqs: [
      { question: "Can I finance just the slide-in vacuum unit?", answer: "Yes! We provide financing for standalone slide-in units, allowing you to convert your existing pickup truck into a service rig without buying a full new chassis." },
      { question: "Do you have programs for aluminum tanks?", answer: "We finance both steel and aluminum vacuum tanks. We understand the value in higher-spec equipment and can adjust terms based on the asset's longevity." }
    ],
    loanRange: "$25,000 - $750,000+",
    customSteps: [
      { n: "1", title: "APPLY", text: "Submit your basic info and equipment quote online." },
      { n: "2", title: "REVIEW", text: "Receive a credit decision in as little as 2 hours." },
      { n: "3", title: "EXECUTE", text: "E-sign your finance documents digitally." },
      { n: "4", title: "WORK", text: "We pay the manufacturer or seller; you take delivery." }
    ],
    ctaHeadline: "DON'T GET STUCK IN THE MUD",
    ctaSubtext: "Get the vacuum equipment your business needs to stay ahead of the demand. Fast approvals, even for used rigs."
  }
};

const steps = [
  { n: "1", title: "APPLY", text: "Submit our simple 5-minute digital app." },
  { n: "2", title: "APPROVE", text: "Get a credit decision in as little as 2 hours." },
  { n: "3", title: "SIGN", text: "E-sign your documents from any device." },
  { n: "4", title: "FUND", text: "We pay the vendor directly within 24-48 hours." }
];

const EquipmentPage = () => {
  const { slug } = useParams();
  const equipment = equipmentData[slug];
  const [openFaq, setOpenFaq] = useState(null);

  // Scroll to top when page loads or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!equipment) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-4xl font-bold mb-4">Equipment Not Found</h1>
          <Link to="/" className="text-[#00A86B] hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 relative overflow-hidden min-h-[500px] md:min-h-[600px]">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${equipment.image})`,
            backgroundPosition: 'center 30%',
          }}
        ></div>
        
        {/* Dark Overlay for Text Readability - lighter on mobile */}
        <div className="absolute inset-0 bg-[#0A1628]/40 md:bg-[#0A1628]/50"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/#we-loan-on" 
            className="inline-flex items-center text-gray-300 hover:text-[#00A86B] mb-6 md:mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Equipment
          </Link>
          
          <div className="max-w-3xl">
            <span className="inline-block bg-[#00A86B] text-white font-bold text-xs md:text-sm tracking-wider px-4 py-2 rounded-lg shadow-lg" style={{ boxShadow: '0 4px 15px rgba(0, 168, 107, 0.5)' }}>{equipment.name.toUpperCase()} FINANCING</span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white mt-3 md:mt-4 mb-4 md:mb-6 leading-tight" style={{ textShadow: '3px 3px 15px rgba(0, 0, 0, 0.7)' }}>
              {equipment.seoHeadline}
            </h1>
            <p className="text-white text-base md:text-lg mb-6 md:mb-8 leading-relaxed" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 0, 0, 0.6)' }}>
              {equipment.description}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button className="bg-[#00A86B] hover:bg-[#008f5b] text-white font-bold px-8 py-4 text-lg rounded-full">
                GET STARTED NOW
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] font-bold px-8 py-4 text-lg rounded-full bg-transparent"
              >
                <Phone className="w-5 h-5 mr-2" />
                1-800-WASTEBRO
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-[#0A1628] text-center mb-4">
            LOAN FEATURES & BENEFITS
          </h2>
          <div className="w-20 h-1 bg-[#00A86B] mx-auto mb-12"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {equipment.features.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div key={i} className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow border-b-4 border-[#00A86B]">
                  <div className="w-12 h-12 bg-[#00A86B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-6 h-6 text-[#00A86B]" />
                  </div>
                  <p className="text-sm uppercase tracking-wider text-gray-500 mb-2 font-semibold">{item.label}</p>
                  <p className="text-lg font-bold text-[#0A1628]">{item.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 20px,
              rgba(255,255,255,0.1) 20px,
              rgba(255,255,255,0.1) 40px
            )`
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-4">
            THE FAST-TRACK FUNDING PROCESS
          </h2>
          <div className="w-20 h-1 bg-[#00A86B] mx-auto mb-16"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {(equipment.customSteps || steps).map((step, i) => (
              <div key={i} className="relative text-center group">
                <div className="text-8xl font-black text-[#00A86B]/20 absolute -top-6 left-1/2 -translate-x-1/2 group-hover:text-[#00A86B]/30 transition-colors">
                  {step.n}
                </div>
                <div className="relative z-10 pt-8">
                  <h3 className="text-2xl font-black text-[#00A86B] mb-3">{step.title}</h3>
                  <p className="text-gray-400">{step.text}</p>
                </div>
                {i < (equipment.customSteps || steps).length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#00A86B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment We Lend On */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00A86B] font-bold text-sm tracking-wider">WHAT WE FINANCE</span>
              <h2 className="text-3xl md:text-4xl font-black text-[#0A1628] mt-4 mb-6">
                EQUIPMENT WE LEND ON
              </h2>
              <p className="text-gray-600 mb-8 text-lg">
                From single units to entire fleets, Waste Loan Bros provides the liquidity you need to scale your business.
              </p>
              <ul className="space-y-4">
                {equipment.inventory.map((item, i) => (
                  <li key={i} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-[#C2A23A]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="w-4 h-4 text-[#C2A23A]" fill="#C2A23A" />
                    </div>
                    <span className="font-medium text-[#0A1628] text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="flex justify-center">
              <div className="w-full max-w-md aspect-square rounded-2xl bg-gray-100 p-8 flex items-center justify-center border-4 border-[#00A86B]/20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_trashcapital/artifacts/i63aa8pe_Text%20only.png" 
                  alt="Waste Loan Bros"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-[#0A1628] text-center mb-4">
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <div className="w-20 h-1 bg-[#00A86B] mx-auto mb-12"></div>
          
          <Accordion 
            type="single" 
            collapsible 
            className="space-y-4"
            onValueChange={(value) => setOpenFaq(value)}
          >
            {equipment.faqs.map((faq, index) => {
              const isOpen = openFaq === `item-${index}`;
              return (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className={`rounded-xl shadow-sm border-none overflow-hidden transition-colors duration-300 ${
                    isOpen ? 'bg-[#0A1628]' : 'bg-white'
                  }`}
                >
                  <AccordionTrigger className={`px-6 py-5 hover:no-underline transition-colors duration-300 ${
                    isOpen ? 'bg-[#0A1628] hover:bg-[#0A1628]' : 'hover:bg-gray-50'
                  }`}>
                    <span className={`font-bold text-lg text-left transition-colors duration-300 ${
                      isOpen ? 'text-white' : 'text-[#0A1628]'
                    }`}>{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className={`px-6 pb-5 pt-0 transition-colors duration-300 ${
                    isOpen ? 'bg-[#0A1628]' : ''
                  }`}>
                    <p className={`transition-colors duration-300 ${
                      isOpen ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#00A86B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 20px,
              rgba(0,0,0,0.1) 20px,
              rgba(0,0,0,0.1) 40px
            )`
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
            {equipment.ctaHeadline || "DON'T WAIT FOR THE NEXT BILLING CYCLE"}
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {equipment.ctaSubtext || "Get the equipment your business needs to fulfill that new contract today."}
          </p>
          <Button className="bg-white text-[#00A86B] hover:bg-gray-100 font-bold px-12 py-5 text-xl rounded-full shadow-lg">
            APPLY FOR FINANCING
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default EquipmentPage;
